import { cn, type TextAreaProps as Props, Textarea as TTextArea } from '@heroui/react';
import debounce from 'lodash.debounce';
import { useEffect, useMemo, useState } from 'react';
import { type Control, Controller, type FieldValues, type Path, type PathValue } from 'react-hook-form';
import { MarkdownEditorModal } from '../Markdown/MarkdownFormTextArea';

export const TextArea = (props: Props) => {
  return (
    <TTextArea
      // classNames={{ input: "text-default-800 placeholder:text-default-400" }}
      radius='sm'
      {...props}
    />
  );
};

export const SearchTextArea = ({ value, onValueChange, ...props }: Props) => {
  const [inputValue, setInputValue] = useState(value ?? '');

  useEffect(() => {
    if (!value) {
      setInputValue('');
      return;
    }
  }, [value]);

  const debouncedOnValueChange = useMemo(() => debounce(onValueChange ?? (() => {}), 1000), [onValueChange]);

  useEffect(() => {
    return () => {
      debouncedOnValueChange.cancel();
    };
  }, [debouncedOnValueChange]);

  // Handle input change
  const handleChange = (val: string) => {
    setInputValue(val);
    debouncedOnValueChange(val);
  };

  return <TextArea {...props} value={inputValue} onValueChange={handleChange} />;
};

export interface FormTextAreaProps<T extends FieldValues> extends Props {
  name: Path<T>;
  control: Control<T, unknown>;
  defaultValue?: PathValue<T, Path<T>>;
  maxCount?: number;
  showMarkdownEditor?: boolean;
}

export const FormTextArea = <T extends FieldValues>({
  control,
  name,
  defaultValue = undefined,
  maxCount,
  className,
  showMarkdownEditor = false,
  endContent,
  ...rest
}: FormTextAreaProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      // shouldUnregister
      defaultValue={defaultValue}
      render={({ field: { onChange, onBlur, value }, fieldState: { error, invalid, isDirty } }) => {
        const count = maxCount ? (value ? value?.trim().split(/\s+/).filter(Boolean).length : 0) : undefined;

        return (
          <TextArea
            endContent={
              <div className='absolute right-2 top-2 flex items-center gap-1'>
                {showMarkdownEditor && (
                  <MarkdownEditorModal title='Markdown Editor' initialValue={value ?? ''} onSave={onChange} />
                )}
                {endContent}
                {maxCount && (
                  <div className=''>
                    <div
                      className={cn(
                        'flex bg-background px-2 py-1 border border-default-300 rounded-md items-center justify-center',
                        {
                          'border-success-500 text-success-600': count >= maxCount * 0.8,
                          'border-warning-500 text-warning-600 ': count > maxCount,
                        },
                      )}
                    >
                      <span className='whitespace-pre text-xs'>
                        {count} / {maxCount}
                      </span>
                    </div>
                  </div>
                )}
                {/* <div>
                  <div className='flex -mt-4 px-2 py-1 border border-default-300 rounded-md items-center justify-center'>
                    <span className='text-default-500 whitespace-pre text-xs'>
                      {count} / {maxCount}
                    </span>
                  </div>
                </div> */}
              </div>
            }
            {...rest}
            className={cn('relative', className)}
            value={value ?? defaultValue}
            onBlur={onBlur}
            onChange={onChange}
            isInvalid={isDirty && invalid}
            errorMessage={error?.message}
          />
        );
      }}
    />
  );
};
